import type { ServiceJournalKind, ServiceTransaction } from '../types';

// Accounting bookings (invoice postings created in AdminAccountingView,
// bookingReference "BU-…") are not service performances — exclude them from
// inspection same-day checks, "last inspection" evaluations and history views.
// Note: "LEGACY-AUTO" and "MANUAL-" stamped entries are genuine work logs and
// must NOT match.
/**
 * Ein VIERTES Praefix mit derselben Aussage, aber mit MENGE: die manuelle
 * Verbuchung ohne Beleg ("MB-…", manualBillingLogic). Jemand hat eine in der
 * Vergangenheit abgerechnete Menge von Hand gestempelt, weil der Beleg dazu
 * fehlt. Sie ist ein Abrechnungsvorgang und keine erbrachte Leistung — ohne
 * diesen Ausschluss laese die naechste Rechnung die eigene Buchung als
 * protokollierte Leistung des Zeitraums und boete die Menge ein zweites Mal
 * an (invoiceLogic.loggedQuantityInPeriod).
 *
 * Zwei weitere Praefixe mit derselben Aussage: die Spur eines VERZICHTS
 * ("VZ-…", serviceWaiverLogic) und die einer RUECKNAHME ("RN-…",
 * serviceReversalLogic). Auch sie sind Buchhaltungsvorgaenge und keine
 * erbrachte Leistung — im einen Fall hat jemand entschieden, eine erbrachte
 * Menge nicht mehr zu fordern, im anderen, dass eine gebuchte Menge nie
 * erbracht wurde.
 *
 * Der Eintrag traegt Menge 0 und veraendert damit ohnehin keine Summe. Er wird
 * hier trotzdem ausgeschlossen, weil an mehreren Stellen ZEILEN gezaehlt und
 * bewertet werden und nicht Mengen: "letzte Begehung", Tagesgleichheit,
 * Historienansichten. Dort waere er ein Begehungstermin, den es nie gab.
 */
export const isAccountingEntry = (log: { bookingReference?: string } | null | undefined): boolean =>
    typeof log?.bookingReference === 'string' &&
    (log.bookingReference.startsWith('BU-') ||
        log.bookingReference.startsWith('VZ-') ||
        log.bookingReference.startsWith('RN-') ||
        log.bookingReference.startsWith('MB-'));

/**
 * Praefix der NACHWEISZEILE auf einer Monatspauschale ("NW-…").
 *
 * Eine Leistung, die auf die Pauschale gebucht wird (Begehungsbericht beim
 * Versand, Nachbuchung von Hand), ist ERBRACHT und gehoert auf den
 * Leistungsnachweis — der Monat der Pauschale wird dadurch aber nicht
 * teurer. Deshalb traegt die Zeile Menge 0: jede Summe, jeder Riegel und
 * jede Monatsregel bleibt unberuehrt (Monate zaehlen nur ueber FLAT-JJJJ-MM),
 * und trotzdem steht der Termin im Protokoll, im Journal und auf dem Blatt.
 *
 * Bewusst KEIN Buchhaltungsvorgang (nicht in isAccountingEntry): die Zeile
 * ist ein echter Leistungstermin und zaehlt dort mit, wo ZEILEN gezaehlt
 * werden — letzte Begehung, Tagesgleichheit, Historie. Die Kennung sitzt in
 * der Referenz und nicht in einem eigenen Feld, damit sie im Journal
 * (`booking_reference`) mitreist und ein spaeterer Journal-Leser sie kennt.
 */
export const FLAT_PROOF_PREFIX = 'NW-';

/** Ist diese Protokollzeile eine Nachweiszeile auf einer Monatspauschale? */
export const isFlatProofEntry = (log: { bookingReference?: string | null } | null | undefined): boolean =>
    typeof log?.bookingReference === 'string' && log.bookingReference.startsWith(FLAT_PROOF_PREFIX);

/**
 * Eine manuelle Verbuchung: jemand hat eine Menge von Hand als abgerechnet
 * gestempelt, ohne dass dabei Arbeit stattgefunden haette.
 *
 * Erkannt wird sie am TEXT, nicht an der Referenz. Das Praefix 'MANUAL-'
 * taugt nicht dafuer — es steht laut der Festlegung oben auch auf echten,
 * von Hand nachgetragenen Leistungen, und die muessen abrechenbar bleiben.
 * Der Leistungsnachweis unterscheidet seit jeher genauso
 * (generatorPerformance.isDrawdownEntry).
 */
export const isManualPosting = (log: { description?: string } | null | undefined): boolean =>
    typeof log?.description === 'string' && /manuelle\s+verbuchung/i.test(log.description);

/**
 * Eine aus einer Alt-Rechnung nachgetragene Leistung ('ALT-…').
 *
 * Sie ist Bestandsuebernahme, kein abrechenbarer Vorgang: der Beleg dafuer ist
 * die Alt-Rechnung, deren Nummer in der Referenz steht. Ohne diesen Ausschluss
 * gilt die Zeile als OFFENER Protokolleintrag, denn sie traegt naturgemaess
 * keine Zuordnung zu einer Rechnung dieser App — die Menge erschien als offene
 * Summe im Monat der Alt-Rechnung, und der Leistungszeitraum der naechsten
 * Rechnung wurde bis dorthin zurueckgezogen (Projekt 6193: Dezember 2025).
 *
 * Das Praefix steht hier bewusst als Literal: diese Datei ist ein Blattmodul
 * ohne LAUFZEIT-Importe (siehe asDay), und externalAdvances zu importieren, wo
 * BACKFILL_REFERENCE_PREFIX definiert ist, machte daraus einen Zyklus.
 * Typ-Importe sind erlaubt: sie verschwinden beim Kompilieren und koennen
 * deshalb keinen Zyklus bilden.
 *
 * Bewusst NICHT Teil von `isAccountingEntry`: fuer den Begehungsrhythmus,
 * die Tagesgleichheits-Pruefungen und die Historie IST die Zeile eine
 * erbrachte Leistung und soll zaehlen. Nur abgerechnet wird sie nicht noch
 * einmal.
 *
 * ─── S5b: die Buchungsart entscheidet, sobald sie bekannt ist ─────────────
 * Das Journal fuehrt genau diese Zeile als `legacy_backfill`
 * (serviceJournal.journalKind: Praefix `ALT-`). Ist die Art bekannt — aus dem
 * Eintrag selbst oder aus dem Buchungsart-Register —, entscheidet ALLEIN sie;
 * sonst gilt unveraendert das Praefix. Die Wege sind deckungsgleich: das
 * Praefix ist die einzige Quelle, aus der `legacy_backfill` ueberhaupt
 * entsteht.
 */
export const isBackfilledAltEntry = (
    log: { id?: string; kind?: ServiceJournalKind; bookingReference?: string } | null | undefined,
): boolean => {
    const art = buchungsartVon(log);
    if (art) return art === 'legacy_backfill';
    return typeof log?.bookingReference === 'string' && log.bookingReference.startsWith('ALT-');
};

// ═══════════════════════════════════════════════════════════════════════════
// Die KIND-FORM einer Buchung im Protokoll der Mutter
// ═══════════════════════════════════════════════════════════════════════════
//
// Bucht eine Tochter auf das LV der Mutter, steht die Zeile im Protokoll der
// MUTTER — und muss dort als Tochter erkennbar sein. Zwei Merkmale tragen das:
// der Ref `RPT-CHILD-<Tochter>-<Nr>` (eindeutig; Journal, Tagesregel und
// Leistungsnachweis lesen ihn) und der Beschreibungstext `Tochter-Projekt
// "<Name>": Bericht Nr. <Nr> (<Menge> <Einheit>)` (die Form aus dem
// Versandpfad; Alteintraege kennen nur sie).
//
// Hier und nicht in reportRebookLogic, weil der Verknuepfungs-Transfer
// (projectLv) dieselbe Form schreiben muss und reportRebookLogic seinerseits
// projectLv importiert — ein Zyklus. Diese Datei importiert nichts.

export const childBookingRef = (childProjectId: string, inspectionNumber: number): string =>
    `RPT-CHILD-${childProjectId}-${inspectionNumber}`;

export const childBookingDescription = (
    childProjectName: string,
    inspectionNumber: number,
    menge: number,
    einheit: string,
): string => `Tochter-Projekt "${childProjectName}": Bericht Nr. ${inspectionNumber} (${menge} ${einheit})`;

/** Die Hauptbuchung einer Begehung — wie `MAIN_BOOKING_DESC` in reportBooking.ts. */
const HAUPTBUCHUNG = /^Bericht #(\d+) abgeschlossen$/;
/** Der allgemeine Ref, Abschnitt GIERIG bis zum letzten Bindestrich (ein sectionId darf welche tragen). */
const RPT_ALLGEMEIN = /^RPT-(.+)-(\d+)$/;
/** Der Kind-Ref. */
const RPT_KIND = /^RPT-CHILD-[0-9a-fA-F-]{36}-(\d+)$/;
/** Die uebrigen Schreibweisen mit Berichtsnummer im Text. */
const NUMMER_IM_TEXT = /(?:Bericht|Begehung|Report)\s*(?:#|Nr\.)\s*(\d{1,9})(?![0-9])/i;

/**
 * Die Berichtsnummer, auf die sich eine Protokollzeile bezieht — oder `null`.
 *
 * Reihenfolge nach Belastbarkeit: der Ref (er wird vom Buchungspfad gesetzt
 * und ist nie freier Text), dann der Beschreibungstext. Buchungen, die kein
 * Bericht sind (Verbuchung, Verzicht, Alt-Rechnung), tragen keine Nummer und
 * liefern `null` — eine ERFUNDENE Nummer waere hier die schlimmere Antwort.
 *
 * S5c: ist der Berichtsbezug der Zeile aus dem Journal bekannt
 * (`berichtsbezugVon`), entscheidet ALLEIN die Spalte `report_number`. Sie
 * entsteht aus denselben beiden sicheren Quellen (Ref, kanonische
 * Hauptbuchung); die Alt-Schreibweisen fuehrt das Journal ohne Nummer, fuer
 * sie liest weiter der Text.
 */
export const reportNumberOf = (
    entry: {
        id?: string;
        reportRef?: string | null;
        description?: string | null;
        bookingReference?: string | null;
        quantity?: number | null;
    } | null | undefined,
): number | null => {
    const bezug = berichtsbezugVon(entry);
    if (bezug) return bezug.nummer;
    const ref = String(entry?.reportRef ?? '');
    const kind = RPT_KIND.exec(ref);
    if (kind) return parseInt(kind[1], 10);
    const allgemein = RPT_ALLGEMEIN.exec(ref);
    if (allgemein) return parseInt(allgemein[2], 10);
    const text = String(entry?.description ?? '');
    const haupt = HAUPTBUCHUNG.exec(text);
    if (haupt) return parseInt(haupt[1], 10);
    const irgendwo = NUMMER_IM_TEXT.exec(text);
    return irgendwo ? parseInt(irgendwo[1], 10) : null;
};

/**
 * Ist diese Zeile die HAUPTBUCHUNG einer Begehung (und nicht Zusatzleistung, Versandvermerk, Verbuchung)?
 *
 * Bleibt in S5c bewusst beim Text: gefragt ist nach der TEXTFORM (projectLv
 * schreibt genau diese Form in die Kind-Form um, der Leistungsnachweis
 * beschriftet danach). Die Spalten koennen sie nicht beantworten — eine
 * `exact`-Zeile mit Ref sagt nicht, ob ihr Text die kanonische Form traegt.
 */
export const isMainInspectionBooking = (
    entry: { description?: string | null } | null | undefined,
): boolean => HAUPTBUCHUNG.test(String(entry?.description ?? ''));

/**
 * Spiegel der generierten Spalte `service_journal.is_posting` (Nr. 59,
 * supabase/service_journal.sql:148). Lockstep: aendert sich die Spalte,
 * aendert sich diese Liste.
 */
const POSTING_KINDS: ReadonlySet<string> = new Set([
    'invoice_posting',
    'manual_posting',
    'waiver',
    'reversal',
]);

// ════════════════════════════════════════════════════════════════════════════
// DAS BUCHUNGSART-REGISTER — die Journal-Spalte fuer Eintraege aus dem JSON
// ════════════════════════════════════════════════════════════════════════════
//
// Die Frage „Buchung oder Arbeit?" beantwortet die Journal-Spalte
// `service_journal.kind`, sobald sie bekannt ist. Bekannt wird sie auf zwei
// Wegen:
//
//   1. AUS DEM EINTRAG — die Zeile stammt aus dem Journal, sie traegt `kind`
//      selbst (journalToServiceTransactions). Dieser Weg hat Vorrang.
//   2. AUS DEM REGISTER — die Zeile stammt aus dem Projekt-JSON und traegt
//      kein `kind`. Beim Oeffnen des Projekts wird das Journal EINMAL gelesen
//      und hier je Eintrags-id die Art hinterlegt
//      (serviceJournal.ladeBuchungsartenInsRegister, gerufen aus App.tsx).
//
// Das JSON wird dabei NIE veraendert: `kind` gelangt nicht in den Blob. Das
// Register liegt daneben im Arbeitsspeicher — ist es leer, gilt unveraendert
// der Praefix- und Textrueckfall, also genau der Stand vor S5b. Genau deshalb
// laesst `ladeBuchungsartenInsRegister` das Register in Ruhe, wenn das Journal
// unbekannt ist: ein leeres Register hiesse „keine Zeile ist eine Buchung",
// und das ist eine Aussage, die niemand getroffen hat.
//
// ─── BEKANNTE GRENZE ──────────────────────────────────────────────────────
// Das Register gilt BIS ZUR NAECHSTEN LADUNG. Aendert ein Pfad einen Eintrag
// an Ort und Stelle, ohne ihm eine neue id zu geben — der LEGACY-AUTO-Stempel
// auf einer Altzeile und dessen Umkehr beim Ent-buchen im Abrechnungsdialog
// (die Stempelreferenz faellt weg) —, so aendert sich die
// hinterlegte Art NICHT mit: work bleibt work. Erst die naechste Ladung stellt
// sie richtig. Wo eine Zeile WIRKLICH verschwindet, fuehrt die Ruecknahme das
// Register dagegen mit (vergissBuchungsartenFuer aus voidJournalEntries).
//
// ─── S5c: DER BERICHTSBEZUG REIST MIT ─────────────────────────────────────
// Dieselbe Ladung hinterlegt je id auch den zerlegten Berichtsbezug der
// Journalzeile (`report_number`, `report_section_id`,
// `report_child_project_id`, `parse_confidence`, `covers_inspection`). Die
// Berichts-Praedikate (reportNumberOf, isReportBooked, isInspectionBilled,
// bookedReportNumbers) fragen ihn ueber `berichtsbezugVon` und fallen auf
// Ref und Text zurueck, wo er fehlt.
//
// Anders als die Art ist der Bezug NICHT ueber alle Staende einer id gleich:
// der Verknuepfungs-Umzug schreibt eine Zeile unter derselben id in die
// Kind-Form um (`RPT-all-7` → `RPT-CHILD-<Tochter>-7`), und eine doppelte id
// im JSON fuehrt zwei verschiedene Buchungen unter einem Schluessel. Deshalb
// traegt jeder hinterlegte Bezug einen FINGERABDRUCK — Ref, Text, Beleg der
// Journalzeile und ob ihre Menge 0 ist — und gilt nur fuer einen Eintrag, der
// genau diese Werte traegt. Damit schliesst sich fuer den Bezug auch die
// BEKANNTE GRENZE oben: ein Eintrag, dessen Ref, Text oder Beleg an Ort und
// Stelle geaendert wurde, passt nicht mehr zum Abdruck und liest wieder den
// Text. (Die Teilruecknahme kuerzt die Menge an Ort und Stelle, aber nie auf
// 0 — die ganze Ruecknahme entfernt die Zeile und vergisst die id.)

/** Eine Journalzeile, so schmal wie das Register sie braucht. */
type RegisterZeile = {
    id: string;
    legacy_entry_id?: string | null;
    kind: ServiceJournalKind;
    voided_at?: string | null;
    // ─── S5c, optional: eine Ladung ohne diese Spalten hinterlegt nur die Art.
    report_ref?: string | null;
    description?: string | null;
    booking_reference?: string | null;
    quantity?: number | string | null;
    report_number?: number | null;
    report_section_id?: string | null;
    report_child_project_id?: string | null;
    parse_confidence?: string | null;
    covers_inspection?: boolean | null;
};

/**
 * Der Berichtsbezug EINER Journalzeile, wie das Register ihn fuehrt — nur fuer
 * die beiden SICHEREN Herkuenfte der Nummer (`exact` aus dem Ref,
 * `text_exact` aus der kanonischen Hauptbuchung, Nr. 119). Eine Zeile mit
 * `heuristic` oder `none` hinterlegt keinen Bezug: fuer sie entscheidet der
 * Text wie vor S5c.
 */
type BerichtsFakten = {
    /**
     * Fingerabdruck: Ref, Text und Beleg der Journalzeile (roh,
     * `abdruck`) und ob ihre Menge 0 ist. Genau das sind die Eingaben, aus
     * denen Nummer UND Deckung entstehen (`parseReportRef`,
     * `inferInspectionCoverage`: Art aus dem Beleg, Deckung nur bei Menge ≠ 0 —
     * ausser der Nachweiszeile einer Monatspauschale, `NW-` mit Berichtsbezug).
     * Passt der Abdruck, beschreiben die Fakten diesen Eintrag — nicht eine
     * andere Buchung unter derselben id.
     */
    ref: string;
    text: string;
    beleg: string;
    mengeNull: boolean;
    nummer: number;
    abschnitt: string | null;
    kindProjekt: string | null;
    ausText: boolean;
    deckt: boolean;
};

/** Was ein Projekt ueber EINE id weiss. */
type RegisterEintrag = {
    kind: ServiceJournalKind;
    /**
     * Stammt die id aus der Spalte `id` (Primaerschluessel der Journalzeile)
     * oder aus `legacy_entry_id` (ihr Verweis auf eine frueher vergebene)?
     */
    primaer: boolean;
    /**
     * S5c — der Bezug JEDER Journalzeile, die dieses Projekt unter der id
     * fuehrt, nicht nur der Zeile, deren Art gilt. Der Vorrang oben ist eine
     * Regel fuer die ART (Primaerschluessel vor Alt-id); fuer den Bezug hiesse
     * er, eine zweite Buchung unter derselben id (doppelte id im JSON, Nr. 60
     * schluesselt sie neu und fuehrt die alte als `legacy_entry_id`) zu
     * verschweigen — und mit ihr den Widerspruch, den `berichtsbezugVon`
     * erkennen soll. Leer, wenn keine Zeile einen sicheren Bezug traegt oder
     * ohne die Spalten geladen wurde.
     */
    fakten: BerichtsFakten[];
};

/**
 * Ref, Text und Beleg so, wie der Fingerabdruck sie vergleicht: ROH, nur
 * fehlend und leer fallen zusammen (''). Bewusst NICHT getrimmt: ein
 * Leerzeichen am Anfang veraendert, was die Praefixpruefungen sehen
 * (`" RN-123"` ist fuer `isAccountingEntry` keine Ruecknahme, fuer das
 * getrimmte Journal der Nachspiegelung schon; `" RPT-all-5"` zerlegt
 * `parseReportRef` nicht). Legen Journal und JSON denselben Wert verschieden
 * ab (Nr. 76 per `btrim`, Client und Nr. 60 roh), passt der Abdruck nicht und
 * der Text entscheidet — die sichere Seite. Ein Nicht-Text ist ''; sein
 * Journaltext ist die JSON-Fassung und passt damit ebenfalls nie.
 */
const abdruck = (wert: unknown): string => (typeof wert === 'string' ? wert : '');

/** Menge 0 — oder keine lesbare Menge (dann deckt die Zeile ohnehin nicht). */
const istMengeNull = (wert: unknown): boolean => {
    const n = Number(wert);
    return !Number.isFinite(n) || n === 0;
};

const faktenAus = (row: RegisterZeile): BerichtsFakten | undefined => {
    const sicherheit = row.parse_confidence;
    if (sicherheit !== 'exact' && sicherheit !== 'text_exact') return undefined;
    const nummer = row.report_number;
    if (typeof nummer !== 'number' || !Number.isInteger(nummer)) return undefined;
    return {
        ref: abdruck(row.report_ref),
        text: abdruck(row.description),
        beleg: abdruck(row.booking_reference),
        mengeNull: istMengeNull(row.quantity),
        nummer,
        abschnitt: typeof row.report_section_id === 'string' ? row.report_section_id : null,
        kindProjekt: typeof row.report_child_project_id === 'string'
            ? row.report_child_project_id.toLowerCase()
            : null,
        ausText: sicherheit === 'text_exact',
        deckt: row.covers_inspection === true,
    };
};

/**
 * id → (Projekt → was dieses Projekt ueber die id weiss).
 *
 * Die zweite Ebene ist nicht Zierde. Dieselbe Zeichenkette kann in MEHREREN
 * Staenden vorkommen — eine Tochter bucht auf das LV der Mutter, der
 * Verknuepfungs-Umzug schreibt Zeilen um, und Zeile A kann `id = U` heissen,
 * waehrend Zeile B dieselbe Zeichenkette als `legacy_entry_id` fuehrt (der
 * Befund `doppelt_im_projekt` der Abweichungssicht). Lag unter U nur EIN
 * Wert, entschied die Reihenfolge der Ladung — und beim Schliessen des einen
 * Projekts behielt die id die Art des GESCHLOSSENEN, obwohl das andere sie
 * laengst anders kennt.
 *
 * Hinterlegt werden BEIDE Schreibweisen einer Zeile: die Journal-id und die
 * urspruengliche JSON-id. Der JSON-Eintrag kennt nur die zweite.
 */
const buchungsartRegister = new Map<string, Map<string, RegisterEintrag>>();
/** Welche ids zu welchem Projekt gehoeren — allein fuers Austragen. */
const registerJeProjekt = new Map<string, Set<string>>();

/** Wie oft welcher Weg entschieden hat (Messung, siehe buchungsartStatistik). */
const buchungsartZaehler = { ausEintrag: 0, ausRegister: 0, rueckfall: 0, konflikte: 0 };

/**
 * Welche Art gilt fuer eine id, ueber alle Projekte hinweg?
 *
 * Reihenfolge: ein PRIMAERSCHLUESSEL schlaegt jede Alt-id — er gehoert der
 * Zeile, die Alt-id ist nur ihr Verweis. Innerhalb desselben Rangs gewinnt
 * das Projekt mit der kleinsten Kennung; das ist willkuerlich, aber
 * BESTIMMT: zwei Laeufe ueber denselben Bestand duerfen nicht verschieden
 * antworten. Widersprechen sich zwei gleichrangige Eintraege, ist das ein
 * KONFLIKT — er wird gezaehlt und nicht verschwiegen (ein gueltiges Journal
 * kann ihn nicht erzeugen; taucht er auf, stimmt etwas mit dem Bestand nicht).
 */
const aufloesen = (eintraege: Map<string, RegisterEintrag>): ServiceJournalKind | undefined => {
    // Der Normalfall, und er soll billig bleiben: genau ein Projekt kennt sie.
    if (eintraege.size === 1) {
        for (const e of eintraege.values()) return e.kind;
        return undefined;
    }
    let gewinnerProjekt = '';
    let gewinnerArt: ServiceJournalKind | undefined;
    let streit = false;
    for (const rang of [true, false]) {
        for (const [projekt, e] of eintraege) {
            if (e.primaer !== rang) continue;
            if (gewinnerArt === undefined) {
                gewinnerProjekt = projekt;
                gewinnerArt = e.kind;
                continue;
            }
            if (e.kind !== gewinnerArt) streit = true;
            if (projekt < gewinnerProjekt) {
                gewinnerProjekt = projekt;
                gewinnerArt = e.kind;
            }
        }
        // Alt-ids kommen nur zum Zug, wenn es keinen Primaerschluessel gibt.
        if (gewinnerArt !== undefined) break;
    }
    if (streit) buchungsartZaehler.konflikte += 1;
    return gewinnerArt;
};

/**
 * Eine id fuer EIN Projekt hinterlegen. `primaer` sagt, ob sie aus der Spalte
 * `id` stammt. Innerhalb desselben Projekts gilt derselbe Vorrang wie
 * zwischen Projekten: eine Alt-id ueberschreibt keinen Primaerschluessel.
 * Zurueck kommt 1, wenn wirklich geschrieben wurde.
 */
const hinterlege = (
    menge: Set<string>,
    projekt: string,
    id: unknown,
    kind: ServiceJournalKind,
    primaer: boolean,
    fakten: BerichtsFakten | undefined,
): number => {
    const wert = typeof id === 'string' ? id.trim() : '';
    if (!wert) return 0;
    // Fuers Austragen gehoert die id zum Projekt — auch dann, wenn ihre Art
    // von der anderen Zeile stammt.
    menge.add(wert);
    let jeProjekt = buchungsartRegister.get(wert);
    if (!jeProjekt) {
        jeProjekt = new Map<string, RegisterEintrag>();
        buchungsartRegister.set(wert, jeProjekt);
    }
    const vorhanden = jeProjekt.get(projekt);
    // Der Bezug geht in jedem Fall mit — auch wenn die Art beim
    // Primaerschluessel bleibt (siehe RegisterEintrag.fakten).
    const alleFakten = [...(vorhanden?.fakten ?? []), ...(fakten ? [fakten] : [])];
    if (vorhanden?.primaer && !primaer) {
        vorhanden.fakten = alleFakten;
        return 0;
    }
    jeProjekt.set(projekt, { kind, primaer, fakten: alleFakten });
    return 1;
};

const mengeFuer = (projekt: string): Set<string> => {
    const vorhanden = registerJeProjekt.get(projekt);
    if (vorhanden) return vorhanden;
    const neu = new Set<string>();
    registerJeProjekt.set(projekt, neu);
    return neu;
};

/**
 * Journalzeilen ZUSAETZLICH hinterlegen — der Weg fuer frisch angehaengte
 * Zeilen (serviceJournal, nach einem gelungenen Anhaengen). Zurueckgenommene
 * Zeilen bleiben draussen: sie stehen nicht mehr im Protokoll und duerfen
 * deshalb auch nichts mehr entscheiden.
 *
 * Zurueck kommt die Zahl der hinterlegten ids — nicht die der Zeilen: eine
 * Zeile mit `legacy_entry_id` zaehlt zweimal, weil unter beiden Schluesseln
 * gesucht wird.
 */
export const ergaenzeBuchungsarten = (
    projectId: string,
    rows: ReadonlyArray<RegisterZeile>,
): number => {
    const projekt = (projectId || '').trim();
    if (!projekt) return 0;
    const menge = mengeFuer(projekt);
    let n = 0;
    for (const row of rows || []) {
        if (!row || row.voided_at) continue;
        if (typeof row.kind !== 'string' || row.kind.length === 0) continue;
        const fakten = faktenAus(row);
        n += hinterlege(menge, projekt, row.id, row.kind, true, fakten);
        n += hinterlege(menge, projekt, row.legacy_entry_id, row.kind, false, fakten);
    }
    return n;
};

/**
 * Den Stand eines Projekts ERSETZEN — der Weg fuer die einmalige Ladung beim
 * Oeffnen. Gelesen wurde der ganze Stand und nicht ein Zuwachs; bliebe die
 * fruehere Hinterlegung daneben stehen, entschieden beim zweiten Oeffnen
 * Zeilen mit, die das Journal inzwischen nicht mehr kennt.
 */
export const registriereBuchungsarten = (
    projectId: string,
    rows: ReadonlyArray<RegisterZeile>,
): number => {
    vergissBuchungsarten(projectId);
    return ergaenzeBuchungsarten(projectId, rows);
};

/**
 * Projekt geschlossen: sein Stand geht mit — und NUR seiner.
 *
 * Getilgt werden die Eintraege dieses Projekts, nicht die id. Dieselbe Zeile
 * kann in zwei Staenden stehen: eine Tochter bucht auf das LV der Mutter, der
 * Verknuepfungs-Umzug schreibt sie um. Die id behaelt danach genau das, was
 * die noch offenen Projekte ueber sie wissen — nicht mehr (sonst entschiede
 * ein geschlossenes Projekt weiter) und nicht weniger (sonst fiele ein
 * offenes Projekt auf Beleg und Text zurueck, obwohl es sein Journal
 * gelesen hat).
 */
export function vergissBuchungsarten(projectId: string): void {
    const projekt = (projectId || '').trim();
    const menge = registerJeProjekt.get(projekt);
    if (!menge) return;
    registerJeProjekt.delete(projekt);
    for (const id of menge) {
        const jeProjekt = buchungsartRegister.get(id);
        if (!jeProjekt) continue;
        jeProjekt.delete(projekt);
        if (jeProjekt.size === 0) buchungsartRegister.delete(id);
    }
}

/**
 * Einzelne ids vergessen — nach einer Ruecknahme (`service_journal_void`)
 * und in `ohneBuchungsart`. Anders als beim Schliessen eines Projekts faellt
 * die id GANZ, ueber alle Staende: die Zeile steht nirgends mehr im
 * Protokoll, und bliebe ihre Art irgendwo hinterlegt, entschiede sie ueber
 * einen JSON-Eintrag weiter, den ein spaeterer Pfad unter derselben id neu
 * geschrieben haben kann.
 */
export const vergissBuchungsartenFuer = (ids: ReadonlyArray<string | null | undefined>): void => {
    for (const roh of ids || []) {
        const id = typeof roh === 'string' ? roh.trim() : '';
        if (!id) continue;
        buchungsartRegister.delete(id);
        for (const menge of registerJeProjekt.values()) menge.delete(id);
    }
};

/** Alles vergessen — Abmeldung und Tests. */
export const leereBuchungsartenRegister = (): void => {
    buchungsartRegister.clear();
    registerJeProjekt.clear();
};

/**
 * Ist das Journal dieses Projekts geladen?
 *
 * Die Frage der Schreibpfade: nur fuer ein Projekt, dessen Stand hier liegt,
 * darf eine frische Buchung nachgeladen werden. Fuer ein fremdes Projekt
 * — der Abrechnungsdialog bucht auf Projekte, die niemand geoeffnet hat —
 * entstuende sonst ein halber Stand, der bis zur Abmeldung liegen bliebe und
 * ueber Zeilen entschiede, die nie jemand gelesen hat. Dort gilt weiter der
 * Praefix- und Textrueckfall.
 */
export const istProjektRegistriert = (projectId: string): boolean =>
    registerJeProjekt.has((projectId || '').trim());

/**
 * Die Buchungsart eines Protokolleintrags, wenn sie bekannt ist: erst aus dem
 * Eintrag selbst, dann aus dem Register. Ein LEERES `kind` ist kein `kind` —
 * es stammt aus einem halb gefuellten Objekt und darf nicht still gegen den
 * Rueckfall entscheiden.
 */
export const buchungsartVon = (
    log: { id?: string; kind?: ServiceJournalKind } | null | undefined,
): ServiceJournalKind | undefined => {
    const eigene = log?.kind;
    if (typeof eigene === 'string' && eigene.length > 0) return eigene;
    const id = typeof log?.id === 'string' ? log.id.trim() : '';
    if (!id) return undefined;
    const eintraege = buchungsartRegister.get(id);
    return eintraege ? aufloesen(eintraege) : undefined;
};

/**
 * Der Berichtsbezug eines Protokolleintrags aus dem Journal (S5c) — was die
 * Spalten ueber den Bericht sagen, auf den die Zeile bucht.
 */
export interface Berichtsbezug {
    /** `report_number`. */
    nummer: number;
    /** `report_section_id` — `'all'` ohne Abschnitte; `null` bei `ausText` und bei Kind-Zeilen. */
    abschnitt: string | null;
    /** `report_child_project_id`: die Zeile bucht den Bericht einer TOCHTER, nicht einen eigenen. */
    kindProjekt: string | null;
    /**
     * `parse_confidence = 'text_exact'`: die Nummer stammt aus der kanonischen
     * Hauptbuchung („Bericht #N abgeschlossen", Nr. 119) und nicht aus dem
     * Ref. Der Text nennt keinen Abschnitt — die Zeile gilt deshalb fuer
     * jeden, genau wie der Textvergleich vor S5c.
     */
    ausText: boolean;
    /**
     * `covers_inspection`: die Zeile weist die BEGEHUNG nach (keine Zusatzleistung, Menge ≠ 0, Arbeit;
     * Menge 0 nur bei der Nachweiszeile einer Monatspauschale, `NW-` mit Berichtsbezug).
     */
    deckt: boolean;
}

/** Wie oft der Bezug entschieden hat — und warum nicht (siehe berichtsbezugStatistik). */
const berichtsZaehler = { ausRegister: 0, rueckfall: 0, fremdeForm: 0, konflikte: 0 };

/**
 * Der Berichtsbezug eines Eintrags — oder `undefined`, dann entscheidet der
 * Aufrufer wie vor S5c nach Ref und Text.
 *
 * Bekannt ist er, wenn das Register zur id des Eintrags eine Journalzeile
 * kennt, deren Nummer SICHER ist (`exact`/`text_exact`) UND deren
 * Fingerabdruck — Ref, Text, Beleg, Menge 0 ja/nein — zu diesem Eintrag
 * passt. Ohne Abdruck
 * spraeche der Bezug einer anderen Form derselben id: der Kind-Form im
 * Protokoll der Mutter die eigene Form der Tochter, einem an Ort und Stelle
 * umgeschriebenen Eintrag seine alte Fassung.
 *
 * Passen mehrere Journalzeilen — aus verschiedenen Staenden oder, bei einer
 * doppelten id, aus demselben — und widersprechen sich (etwa Menge 0 und 1
 * unter gleichem Ref und Text: die eine deckt, die andere nicht), ist das ein
 * KONFLIKT: er wird
 * gezaehlt, und der Text entscheidet — eine willkuerlich gewaehlte Seite waere
 * hier eine erfundene Antwort.
 */
export const berichtsbezugVon = (
    entry: {
        id?: string;
        reportRef?: string | null;
        description?: string | null;
        bookingReference?: string | null;
        quantity?: number | null;
    } | null | undefined,
): Berichtsbezug | undefined => {
    const id = typeof entry?.id === 'string' ? entry.id.trim() : '';
    const eintraege = id ? buchungsartRegister.get(id) : undefined;
    if (!eintraege) {
        berichtsZaehler.rueckfall += 1;
        return undefined;
    }
    const ref = abdruck(entry?.reportRef);
    const text = abdruck(entry?.description);
    const beleg = abdruck(entry?.bookingReference);
    const mengeNull = istMengeNull(entry?.quantity);
    let treffer: BerichtsFakten | undefined;
    let mitFakten = false;
    let streit = false;
    for (const e of eintraege.values()) {
        for (const f of e.fakten) {
            mitFakten = true;
            if (f.ref !== ref || f.text !== text || f.beleg !== beleg || f.mengeNull !== mengeNull) continue;
            if (!treffer) {
                treffer = f;
            } else if (
                f.nummer !== treffer.nummer
                || f.abschnitt !== treffer.abschnitt
                || f.kindProjekt !== treffer.kindProjekt
                || f.ausText !== treffer.ausText
                || f.deckt !== treffer.deckt
            ) {
                streit = true;
            }
        }
    }
    if (streit) {
        berichtsZaehler.konflikte += 1;
        return undefined;
    }
    if (!treffer) {
        if (mitFakten) berichtsZaehler.fremdeForm += 1;
        else berichtsZaehler.rueckfall += 1;
        return undefined;
    }
    berichtsZaehler.ausRegister += 1;
    return {
        nummer: treffer.nummer,
        abschnitt: treffer.abschnitt,
        kindProjekt: treffer.kindProjekt,
        ausText: treffer.ausText,
        deckt: treffer.deckt,
    };
};

/**
 * Wie oft hat der Berichtsbezug entschieden, und wie oft nicht?
 *
 * - `ausRegister`: die Spalten haben entschieden.
 * - `rueckfall`: kein sicherer Bezug bekannt (id nicht registriert, oder die
 *   Zeile fuehrt `heuristic`/`none`) — der Text entschied, wie vor S5c.
 * - `fremdeForm`: bekannt, aber fuer eine ANDERE Form der id (Umzug,
 *   Umschreibung an Ort und Stelle, doppelte id) — der Text entschied.
 * - `konflikte`: zwei passende Staende widersprechen sich. Ueber Null heisst:
 *   am Bestand stimmt etwas nicht.
 */
export const berichtsbezugStatistik = (): {
    ausRegister: number;
    rueckfall: number;
    fremdeForm: number;
    konflikte: number;
} => ({ ...berichtsZaehler });

/** Nur die Zaehler zuruecksetzen — das Register bleibt stehen. */
export const resetBerichtsbezugStatistik = (): void => {
    berichtsZaehler.ausRegister = 0;
    berichtsZaehler.rueckfall = 0;
    berichtsZaehler.fremdeForm = 0;
    berichtsZaehler.konflikte = 0;
};

/**
 * Wie oft hat welcher Weg entschieden — und wie gross ist das Register?
 *
 * `registriert` (ids mit mindestens einem Eintrag) und `projekte`
 * beschreiben den STAND (sie werden von `resetBuchungsartStatistik` nicht
 * angefasst), die vier Zaehler den Verlauf. `rueckfall` ist die Zahl, auf die
 * es ankommt: sie sagt, wie oft Beleg und Text noch entscheiden mussten.
 * `konflikte` zaehlt ENTSCHEIDUNGEN ueber eine id, ueber die sich zwei
 * gleichrangige Staende nicht einig waren — nicht die Zahl solcher ids.
 * Ueber Null darf sie nicht stehen; tut sie es, stimmt etwas am Bestand nicht.
 */
export const buchungsartStatistik = (): {
    ausEintrag: number;
    ausRegister: number;
    rueckfall: number;
    konflikte: number;
    registriert: number;
    projekte: number;
} => ({
    ...buchungsartZaehler,
    registriert: buchungsartRegister.size,
    projekte: registerJeProjekt.size,
});

/** Nur die Zaehler zuruecksetzen — das Register bleibt stehen. */
export const resetBuchungsartStatistik = (): void => {
    buchungsartZaehler.konflikte = 0;
    buchungsartZaehler.ausEintrag = 0;
    buchungsartZaehler.ausRegister = 0;
    buchungsartZaehler.rueckfall = 0;
};

/**
 * Buchung statt Arbeit — die Zusammenfassung beider Faelle.
 *
 * Wer fragt "ist das eine erbrachte Leistung, die abgerechnet werden kann?",
 * muss ALLE DREI ausschliessen — die dritte ist die aus einer Alt-Rechnung
 * nachgetragene Zeile, die bereits berechnet wurde (isBackfilledAltEntry). `isAccountingEntry` allein reichte nicht: in
 * Projekt 5606 zaehlte eine manuelle Verbuchung ueber 7 Stueck als sieben
 * Begehungen mit. In der Monatsuebersicht zehrte der bereits abgerechnete
 * Bestand deshalb sieben Eintraege zu frueh auf, und die offenen Begehungen
 * erschienen im Mai und Juni statt im Juli und August.
 *
 * ─── S5b: die Buchungsart entscheidet, sobald sie bekannt ist ─────────────
 * Ist die Journal-Spalte `service_journal.kind` fuer diese Zeile bekannt,
 * entscheidet ALLEIN sie. Bekannt ist sie auf zwei Wegen, in dieser
 * Reihenfolge: AUS DEM EINTRAG (die Zeile stammt selbst aus dem Journal —
 * Journal-Leser) und AUS DEM REGISTER (eine Zeile aus dem Projekt-JSON, zu
 * deren id beim Oeffnen des Projekts das Journal gelesen wurde; siehe das
 * Buchungsart-Register oben). JSON-Eintraege werden dabei NIE veraendert:
 * `kind` gelangt nie in den Blob.
 *
 * Ist sie UNBEKANNT — kein Register geladen, id dort nicht enthalten —, gilt
 * unveraendert der Praefix- und Textrueckfall. Beide Wege beantworten dieselbe
 * Frage; die einzige bekannte Abweichungsklasse ist die Zeile mit `ALT-`-Beleg
 * UND dem Text „manuelle Verbuchung": der Rueckfall nennt sie Buchung, das
 * Journal nennt sie `legacy_backfill` und damit keine. Das Journal hat recht —
 * die Bestandsuebernahme IST fuer Rhythmus, Tagesgleichheit und Historie
 * erbrachte Leistung (siehe isBackfilledAltEntry).
 *
 * Die Grenze des Registers steht bei seiner Erklaerung: es gilt bis zur
 * naechsten Ladung, und eine Aenderung an Ort und Stelle ohne neue id
 * (LEGACY-AUTO-Stempel, Ent-buchen) aendert die Art nicht mit.
 */
export const isPostingEntry = (
    log: { id?: string; kind?: ServiceJournalKind; bookingReference?: string; description?: string } | null | undefined,
): boolean => {
    const art = buchungsartVon(log);
    if (art) {
        if (typeof log?.kind === 'string' && log.kind.length > 0) buchungsartZaehler.ausEintrag += 1;
        else buchungsartZaehler.ausRegister += 1;
        return POSTING_KINDS.has(art);
    }
    buchungsartZaehler.rueckfall += 1;
    return isAccountingEntry(log) || isManualPosting(log);
};

/**
 * Die BELEG-Arten: hinter ihnen steht ein Dokument, das man vorzeigen kann.
 * Echte Teilmenge von POSTING_KINDS.
 */
const BELEG_KINDS: ReadonlySet<string> = new Set(['invoice_posting', 'manual_posting']);

/**
 * Eine Buchung MIT BELEG — fuer Belegdialoge.
 *
 * Gedacht fuer Ansichten, die BELEGE auflisten (AdminAccountingView,
 * „Buchungsbelege & Historie"): Verzicht (`VZ-`) und Ruecknahme (`RN-`) SIND
 * Buchungen — `isPostingEntry` nennt sie zu Recht so —, aber keine Belege. Sie
 * tragen Menge 0 und kein Dokument; in einer Belegliste waeren sie eine
 * Buchung, zu der es nichts zu zeigen gibt.
 *
 * Entschieden wird wie bei `isPostingEntry`: erst die Journal-Buchungsart (aus
 * dem Eintrag, dann aus dem Register — dieselbe Statistik zaehlt mit), sonst
 * der Rueckfall auf Beleg und Text. Der Rueckfall nennt genau die beiden
 * Praefixe, die auf die beiden Beleg-Arten abbilden: `BU-` (Rechnung/Storno →
 * invoice_posting) und `MB-` (manuelle Verbuchung → manual_posting), dazu den
 * Text „manuelle Verbuchung" fuer die Zeilen ohne Praefix.
 */
export const istBelegBuchung = (
    log: { id?: string; kind?: ServiceJournalKind; bookingReference?: string; description?: string } | null | undefined,
): boolean => {
    const art = buchungsartVon(log);
    if (art) {
        if (typeof log?.kind === 'string' && log.kind.length > 0) buchungsartZaehler.ausEintrag += 1;
        else buchungsartZaehler.ausRegister += 1;
        return BELEG_KINDS.has(art);
    }
    buchungsartZaehler.rueckfall += 1;
    const ref = typeof log?.bookingReference === 'string' ? log.bookingReference : '';
    return ref.startsWith('BU-') || ref.startsWith('MB-') || isManualPosting(log);
};

/**
 * Vorlage fuer einen NEUEN Eintrag (Spread, Storno, Umhaengung): die
 * Journal-Buchungsart wird NICHT mitkopiert — sie wird beim Schreiben aus dem
 * Inhalt abgeleitet (serviceJournal.journalKind), nie vererbt. Die Eingabe
 * bleibt unveraendert.
 *
 * Gebraucht wird der Helfer ueberall, wo aus einer bestehenden Zeile eine neue
 * entsteht UND deren Einstufungsgrundlage sich aendert (`bookingReference`
 * oder `description`). Bliebe das alte `kind` stehen, entschiede
 * `isPostingEntry` weiter nach der ALTEN Zeile: eine ent-buchte Leistung
 * bliebe `invoice_posting` und fiele aus jeder Abrechnung.
 *
 * ─── Das Register muss mit ────────────────────────────────────────────────
 * Das `kind` zu streichen genuegt nicht, solange die ID BLEIBT: genau das tut
 * ein Pfad, der einer Zeile an Ort und Stelle die Referenz nimmt (heute die
 * Umkehr des LEGACY-AUTO-Stempels; bis zum 24.09.2026 auch das Ent-buchen
 * von `VZ-`/`RN-`-Spuren). Das Register spraeche fuer diese id weiter von der
 * alten Art, und die Zeile bliebe, was sie war —
 * die Vorlage waere wirkungslos. Deshalb vergisst der Helfer die id: die neue
 * Form entscheidet bis zur naechsten Ladung nach ihrem EIGENEN Inhalt, und
 * die naechste Ladung holt die Wahrheit aus dem Journal zurueck.
 */
export const ohneBuchungsart = <T extends { kind?: ServiceJournalKind; id?: string }>(
    entry: T,
): Omit<T, 'kind'> => {
    const kopie = { ...entry };
    delete kopie.kind;
    vergissBuchungsartenFuer([entry?.id]);
    return kopie;
};

// ════════════════════════════════════════════════════════════════════════════
// DER ALT-STEMPEL DER VERBUCHUNG (AdminAccountingView, „Verbuchen")
// ════════════════════════════════════════════════════════════════════════════
//
// Beim Verbuchen einer Position werden deren Leistungszeilen ohne `bookedAt`
// als abgerechnet GESTEMPELT. Bis zum 24.09.2026 schrieb der Stempel jeder
// solchen Zeile `bookingReference: 'LEGACY-AUTO'` — und ueberschrieb damit
// eine vorhandene Referenz: aus `ALT-…` (Alt-Rechnungs-Uebernahme) wurde
// Arbeit, `revertBackfill` fand den Import nicht mehr, und das Journal fuehrte
// die Zeile weiter unter ihrer alten Referenz (Blob und Journal liefen
// auseinander).
//
// Seitdem gilt: eine VORHANDENE Referenz bleibt samt Buchungsart stehen, der
// Stempel setzt nur `bookedAt`/`bookedBy`. Nur eine Zeile OHNE Referenz
// bekommt weiterhin 'LEGACY-AUTO'. Das Merkmal „gestempelt" steckt deshalb
// nicht mehr allein in der Referenz, sondern im Verbucher
// (`bookedBy = 'System (Legacy)'`) — `isLegacyStamped` fragt beides.
//
// Bewusst KEIN eigenes Feld (etwa `stampedAt`): `bookedBy` traegt den Wert
// ohnehin auf JEDER je gestempelten Zeile (alt wie neu), wird ins Journal
// gespiegelt (`booked_by`) und von der Reparatur-SQL gleich gelesen; ein
// neues Feld kennten weder Altbestand noch Server.

/** Die Referenz, die eine gestempelte Zeile OHNE eigene Referenz bekommt. */
export const LEGACY_STAMP_REFERENCE = 'LEGACY-AUTO';
/** Der Verbucher, den der Stempel einträgt — das Stempelmerkmal. */
export const LEGACY_STAMP_ACTOR = 'System (Legacy)';

/**
 * Wurde diese Zeile beim Verbuchen nachtraeglich als abgerechnet gestempelt?
 *
 * Alte Zeilen tragen 'LEGACY-AUTO' (die Referenz ist beim Stempeln verloren
 * gegangen), neue ihre urspruengliche Referenz und `bookedBy = 'System
 * (Legacy)'`. Die Referenz wird wie im Leistungsnachweis normalisiert
 * verglichen (Altbestand mit Leerzeichen/Kleinschreibung).
 */
export const isLegacyStamped = (
    log: { bookingReference?: string; bookedBy?: string } | null | undefined,
): boolean =>
    (typeof log?.bookingReference === 'string' &&
        log.bookingReference.trim().toUpperCase() === LEGACY_STAMP_REFERENCE) ||
    log?.bookedBy === LEGACY_STAMP_ACTOR;

/**
 * Stempelt EINE Zeile als abgerechnet — sofern sie noch kein `bookedAt` hat.
 *
 * - Mit Referenz: nur `bookedAt` (= Leistungsdatum) und `bookedBy`; Referenz,
 *   `kind` und alles andere bleiben unveraendert.
 * - Ohne Referenz: zusaetzlich `bookingReference: 'LEGACY-AUTO'` (wie bisher).
 * - Schon gebucht (`bookedAt` gesetzt): dieselbe Zeile zurueck.
 */
export const stampLegacyEntry = (t: ServiceTransaction): ServiceTransaction => {
    if (t.bookedAt) return t;
    // Ein vorhandenes `bookedBy` ohne `bookedAt` ginge hier verloren — kein Erzeuger schreibt diese Form.
    const ref = typeof t.bookingReference === 'string' ? t.bookingReference.trim() : '';
    if (ref) return { ...t, bookedAt: t.date, bookedBy: LEGACY_STAMP_ACTOR };
    // `ohneBuchungsart`: die Zeile bekommt eine NEUE Referenz — eine aus dem
    // Journal mitgebrachte Art beschriebe ab hier die alte (Bestand).
    return { ...ohneBuchungsart(t), bookedAt: t.date, bookedBy: LEGACY_STAMP_ACTOR, bookingReference: LEGACY_STAMP_REFERENCE };
};

/** Stempelt alle ungebuchten Zeilen der gewaehlten Positionen; der Rest bleibt identisch. */
export const stampLegacyEntries = (
    log: ReadonlyArray<ServiceTransaction>,
    selectedServiceIds: ReadonlySet<string>,
): ServiceTransaction[] =>
    log.map((t) => (selectedServiceIds.has(t.serviceId) ? stampLegacyEntry(t) : t));

/**
 * Das Ent-buchen EINER Position (Storno im Abrechnungsdialog,
 * `handleRestorePosition`), rein.
 *
 * Zurueckgenommen wird GENAU das, was die Verbuchung (`handleExecuteBilling`)
 * geschrieben hat — ihre `BU-`-Buchungszeile und ihren Stempel auf den
 * ungebuchten Zeilen. Zeilen, die ein ANDERER Vorgang geschrieben hat, bleiben
 * unberuehrt: Alt-Rechnung (`ALT-`), manuelle Verbuchung ohne Beleg (`MB-`,
 * eigene Ruecknahme planManualBillingUndo), Verzicht (`VZ-`), Ruecknahme
 * (`RN-`), Monatspauschale (`FLAT-YYYY-MM`, der Schluessel IST der Riegel
 * gegen den doppelten Monat), Einweisung (`UW-`), `MANUAL-` ohne Verbuchungstext und die
 * Berichtsbuchung (reportBooking setzt `bookedAt` selbst). Bis zum 24.09.2026
 * nahm das Ent-buchen jeder solchen Zeile mit `bookedAt` alle Buchungsfelder
 * samt Referenz und Art — aus einer Buchung oder Bestandsuebernahme wurde
 * Arbeit, und das Journal (es kennt kein Update) fuehrte sie weiter unter
 * ihrer alten Referenz.
 *
 * - Zeilen anderer Positionen: unveraendert.
 * - `BU-…` und `MANUAL-…` MIT Verbuchungstext (Buchungszeilen der Verbuchung,
 *   neu und vor 1a4e5be): entfernt, ihre id in `removedIds`,
 *   ihre Menge (mit Vorzeichen) in `removedQuantity`.
 * - Referenz 'LEGACY-AUTO' (der Stempel ohne eigene Referenz, alt wie neu):
 *   alle Buchungsfelder und die Buchungsart weg — wie bisher.
 * - Stempelverbucher 'System (Legacy)' mit erhaltener Referenz (die neue
 *   Stempelform): nur `bookedAt`/`bookedBy` fallen weg — exakt die Umkehr von
 *   `stampLegacyEntry`. Kein anderer Pfad schreibt diesen Verbucher, und der
 *   Stempel trifft nur Zeilen OHNE `bookedAt`; eine Buchungsreferenz
 *   (BU-/MB-/VZ-/RN-) wird deshalb nie als Stempel gelesen.
 * - Alles andere: dieselbe Zeile (dasselbe Objekt).
 */
export const planPositionRestore = (
    log: ReadonlyArray<ServiceTransaction>,
    serviceId: string,
): { log: ServiceTransaction[]; removedIds: string[]; removedQuantity: number } => {
    const removedIds: string[] = [];
    const out: ServiceTransaction[] = [];
    let removedQuantity = 0;
    for (const t of log) {
        if (t.serviceId !== serviceId) { out.push(t); continue; }
        const ref = typeof t.bookingReference === 'string' ? t.bookingReference : '';
        // `MANUAL-…` MIT dem Verbuchungstext ist die Buchungszeile der
        // Verbuchung aus der Zeit vor `BU-` (bis 1a4e5be schrieb
        // handleExecuteBilling genau diese Form) — sie wird wie `BU-`
        // zurueckgenommen. Ohne den Text ist `MANUAL-` echte Arbeit und bleibt.
        if (ref.startsWith('BU-') || (ref.startsWith('MANUAL-') && isManualPosting(t))) {
            if (typeof t.id === 'string' && t.id.trim()) removedIds.push(t.id.trim());
            removedQuantity += Number(t.quantity) || 0;
            continue;
        }
        if (ref.trim().toUpperCase() === LEGACY_STAMP_REFERENCE) {
            const { bookedAt: _bookedAt, bookedBy: _bookedBy, bookedById: _bookedById, bookingReference: _bookingReference, ...rest } = t;
            // Die Referenz stammt vom Stempel; mit ihr faellt die Buchungsart
            // (sie beschriebe sonst die gestempelte Form).
            out.push(ohneBuchungsart(rest));
            continue;
        }
        if (t.bookedBy === LEGACY_STAMP_ACTOR && ref.trim() && !isAccountingEntry(t)) {
            const { bookedAt: _bookedAt, bookedBy: _bookedBy, ...rest } = t;
            out.push(rest);
            continue;
        }
        out.push(t);
    }
    return { log: out, removedIds, removedQuantity };
};

/**
 * Der Abrechnungsstand einer Position NACH dem Ent-buchen.
 *
 * Gerechnet mit denselben Regeln, mit denen die Vorwaertspfade den Zaehler
 * bewegen:
 *   - Rueckschreibungen (BU-, MB-, VZ-, RN-) ADDIEREN mit Vorzeichen
 *     (handleExecuteBilling, manualBillingLogic) — ihre Summe ist
 *     `postedQuantity`.
 *   - Eine Alt-Rechnung HEBT den Zaehler auf ihre Menge, sie addiert nicht
 *     (externalAdvances.nextBilled: `max(current, documentQuantity)`).
 *
 * Was die verbleibenden Zeilen danach MINDESTENS belegen, ist deshalb
 * `max(postedQuantity(rest), ALT-Menge(rest))` — unabhaengig davon, in
 * welcher Reihenfolge sie entstanden sind; hoechstens belegen sie die Summe.
 *
 *     nachher = min(vorher, max(untergrenze, min(vorher − entfernt, posted + alt)))
 *
 * - Nur Verbuchungszeilen (BU-, MANUAL- mit Verbuchungstext): Untergrenze
 *   und Summe sind 0 — der Stand faellt auf 0 wie bisher (auch ein
 *   Altzaehler ohne Buchungszeile, Vorfall 6172).
 * - Nie unter `postedQuantity(rest)`: sonst meldete die Projektpruefung
 *   `billed_below_postings`, obwohl der Storno das Protokoll so hinterlaesst.
 * - Nie unter die ALT-Menge: eine BU-Verbuchung, die eine Alt-Rechnung
 *   spaeter ueberholt hat, gibt beim Ent-buchen keine Menge frei, die der
 *   Altbeleg laengst gefordert hat.
 * - Nie ueber `vorher`: ein Storno hebt keinen Zaehler an.
 *
 * Bewusst die Regel des UNGETEILTEN Projekts: bei geteilten Projekten hebt
 * nextBilled je Auftraggeber an; die Untergrenze aus der Gesamtmenge ist dort
 * hoechstens zu hoch, und `vorher` deckelt sie.
 */
export const billedAfterPositionRestore = (
    billedBefore: number,
    restLog: ReadonlyArray<ServiceTransaction>,
    removedQuantity: number,
    serviceId: string,
): number => {
    const vorher = Math.max(0, Number(billedBefore) || 0);
    const ohneVerbuchung = Math.max(0, vorher - (Number(removedQuantity) || 0));
    const alt = Math.max(
        0,
        (restLog || [])
            .filter((t) => t.serviceId === serviceId && !isAccountingEntry(t) && isBackfilledAltEntry(t))
            .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0),
    );
    const posted = Math.max(0, postedQuantity(restLog, serviceId));
    const untergrenze = Math.max(posted, alt);
    const nachher = Math.min(vorher, Math.max(untergrenze, Math.min(ohneVerbuchung, posted + alt)));
    return Math.round(nachher * 1000) / 1000;
};

/**
 * Was das Protokoll fuer EINE Position an LEISTUNG ausweist.
 *
 * Buchungseintraege zaehlen nicht mit — sie beschreiben die Abrechnung, nicht
 * die Arbeit. Gerechnet wird mit Vorzeichen: eine zurueckgenommene Menge steht
 * als negativer Eintrag im Protokoll und senkt die Summe. Nur die positiven zu
 * zaehlen liesse jede Ruecknahme als Fehlbetrag erscheinen.
 *
 * Die Summe ist die gemeinsame Grundlage zweier Aussagen, die
 * auseinanderzulaufen droht, wenn jede sie selbst bildet: der gelbe Befund
 * `unbilled_logged_quantity` der Projektpruefung und der Riegel
 * `logged_exceeds_used` am Beleg (invoiceLogic).
 */
export const loggedQuantity = (
    log: ReadonlyArray<{ serviceId?: string; quantity?: number; bookingReference?: string }> | null | undefined,
    serviceId: string,
): number =>
    (log || [])
        .filter((t) => t.serviceId === serviceId && !isAccountingEntry(t))
        .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);

/**
 * Was das Protokoll fuer EINE Position an ABRECHNUNG ausweist — die
 * Gegenrechnung zu `loggedQuantity`.
 *
 * Summiert werden die Buchungsvorgaenge an der REFERENZ (`isAccountingEntry`:
 * BU- Rechnung/Storno, MB- manuelle Verbuchung, VZ- Verzicht, RN- Ruecknahme),
 * mit Vorzeichen: eine Rechnung schreibt +Menge, ein Storno oder eine
 * Gutschrift −Menge, Verzicht und Ruecknahme-Spuren 0. Die Summe ist der
 * Abrechnungsstand, den das Protokoll belegt, und muss `quantityBilled`
 * entsprechen — sinkt der Zaehler darunter, hat ein Speichervorgang eine
 * Rueckschreibung verloren (26051026, 07.09.2026: eine alte Sitzung schrieb
 * 4 → 1, die Schlussrechnung hatte die 4 laengst gefordert).
 *
 * BEWUSST NICHT `isPostingEntry`, und zwar wegen seines Textzweigs
 * („manuelle Verbuchung") und des Journal-`kind`:
 *   - Bis zum 24.09.2026 streifte das Ent-buchen im Abrechnungsdialog
 *     (AdminAccountingView) einer MB-Zeile Referenz und Buchungsfelder ab,
 *     der Text blieb stehen. Solche Altzeilen gibt es im Bestand; mit dem
 *     Textzweig deckten sie einen Stand, den niemand mehr fordert. Seitdem
 *     laesst das Ent-buchen MB-Zeilen stehen und behaelt ihre Menge im
 *     Zaehler (billedAfterPositionRestore).
 *   - Eine `ALT-`-Zeile mit diesem Text ist Bestandsuebernahme (Journal:
 *     legacy_backfill), keine Abrechnung — `isPostingEntry` liest sie ohne
 *     kind als Buchung, mit kind nicht. `ALT-` traegt `quantityBilled` nie.
 *   - Die 100 alten `MANUAL-`-Zeilen mit dem Text haben den Zaehler nie
 *     verlaesslich mitgeschrieben; sie zaehlen hier nicht, und weil die Regel
 *     nur die Richtung „Zaehler UNTER Summe" meldet, entsteht daraus kein
 *     Befund. Die Gegenrichtung (Vorfall 6172) ist bewusst keiner.
 * Genau dasselbe Praedikat zieht der Server in Nr. 111
 * (`project_services_billed_guard`): zwei Begriffe liefen auseinander.
 */
export const postedQuantity = (
    log: ReadonlyArray<{ serviceId?: string; quantity?: number; bookingReference?: string }> | null | undefined,
    serviceId: string,
): number =>
    (log || [])
        .filter((t) => t.serviceId === serviceId && isAccountingEntry(t))
        .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);

/**
 * Dieselbe Summe OHNE die aus einer Alt-Rechnung nachgetragene Menge.
 *
 * Der Unterschied ist der zwischen einem Hinweis und einer Sperre. Die
 * `ALT-`-Zeile traegt in EINEM Eintrag den Bestand einer Alt-Rechnung; stehen
 * die einzelnen Leistungen daneben auch im Protokoll, zaehlt sie dieselbe
 * Arbeit ein zweites Mal. In Projekt 6106 sind es 39 uebernommene neben 45
 * protokollierten Begehungen: mit der Uebernahme gerechnet stuenden 84
 * "erbrachte" gegen einen Leistungsstand von 45, und ein Riegel darauf haette
 * das Projekt gesperrt, obwohl sein Stand stimmt.
 *
 * BEWUSSTE BLINDSTELLE: die Alt-Menge kann auch DISJUNKT sein — in 5940 waren
 * die 42,5 Stunden des Belegs und die 45 protokollierten zwei verschiedene
 * Leistungen, erbracht also 87,5. Dort uebersieht diese Summe einen echten
 * Fehlstand. Das ist die richtige Richtung fuer eine Sperre: eine uebersehene
 * Luecke meldet die Projektpruefung weiterhin gelb (`unbilled_logged_quantity`
 * rechnet mit `loggedQuantity`), eine grundlos gesperrte Rechnung dagegen
 * haelt den Betrieb an.
 */
export const ownLoggedQuantity = (
    log: ReadonlyArray<{ serviceId?: string; quantity?: number; bookingReference?: string }> | null | undefined,
    serviceId: string,
): number =>
    (log || [])
        .filter((t) => t.serviceId === serviceId && !isAccountingEntry(t) && !isBackfilledAltEntry(t))
        .reduce((sum, t) => sum + (Number(t.quantity) || 0), 0);

/**
 * Toleranz fuer den Vergleich Protokollsumme gegen Leistungsstand.
 *
 * Dieselbe Groesse, mit der die Projektpruefung eine Ueberabrechnung misst:
 * eine ueber mehrere Teilbelege geteilte Menge landet ein Vielfaches von 1e-17
 * neben der erbrachten. Das ist Gleitkommastaub, kein Geld — und ein Riegel,
 * der daran eine Rechnung sperrt, waere nicht zu erklaeren.
 */
export const LOGGED_QUANTITY_EPSILON = 0.001;

/** Eine Position, deren Protokoll mehr ausweist, als im Leistungsstand steht. */
export interface MissingUsedQuantity {
    serviceId: string;
    lvPosition?: string;
    title?: string;
    /** Summe der Leistungszeilen im Protokoll. */
    logged: number;
    /** Was der Leistungsstand traegt (quantityUsed). */
    used: number;
    /** Was fehlt — immer groesser als die Toleranz. */
    missing: number;
}

/**
 * Positionen, bei denen erbrachte Leistung NICHT im Leistungsstand steht.
 *
 * Die Richtung ist die teure: abgerechnet wird, was im Leistungsstand steht,
 * und der hinkt hinterher. In Projekt 6106 blieb so die Begehung zu Bericht
 * #45 aus der Schlussrechnung — Bericht, Archiv und Protokollzeile waren
 * vollstaendig, allein `quantityUsed` war nie fortgeschrieben worden.
 *
 * Die andere Richtung (gebucht ohne Protokollspur) steht bewusst NICHT hier:
 * sie ist ein Hinweis und war nie eine Sperre, weil die Menge erbracht IST und
 * nur der Nachweis fehlt (projectCheckLogic, `unlogged_quantity`).
 */
export const findMissingUsedQuantity = (
    services: ReadonlyArray<{ id: string; lvPosition?: string; title?: string; quantityUsed?: number }> | null | undefined,
    log: ReadonlyArray<{ serviceId?: string; quantity?: number; bookingReference?: string }> | null | undefined,
): MissingUsedQuantity[] => {
    const out: MissingUsedQuantity[] = [];
    for (const s of services || []) {
        if (!s?.id) continue;
        const used = Number(s.quantityUsed) || 0;
        // OHNE die Alt-Uebernahme — Begruendung und die bewusste Blindstelle
        // stehen an ownLoggedQuantity.
        const logged = ownLoggedQuantity(log, s.id);
        const missing = logged - used;
        if (missing > LOGGED_QUANTITY_EPSILON) {
            out.push({ serviceId: s.id, lvPosition: s.lvPosition, title: s.title, logged, used, missing });
        }
    }
    return out;
};

/**
 * Kalendertag eines Datums aus dem Leistungsprotokoll.
 *
 * Die Eintraege tragen dort volle Zeitstempel ("2026-07-31T14:39:00.000Z"),
 * ein Zeitraum dagegen reine Tage ("2026-07-31"). Beide als Zeichenkette zu
 * vergleichen ging fuer jeden Tag AUSSER dem letzten gut: bei gleichem Anfang
 * gilt die laengere Zeichenkette als groesser, der Zeitstempel lag also
 * "nach" dem Ende des Zeitraums.
 *
 * Damit fiel jede Leistung am letzten Tag eines Monats aus diesem Monat heraus
 * — und aus dem naechsten ebenso, weil sie vor dessen Beginn liegt. Ueber
 * einen Zeitraumfilter war sie nie abrechenbar.
 *
 * Diese Datei ist bewusst der Ort dafuer: sie ist ein Blattmodul ohne eigene
 * Importe. invoiceLogic und allocationLogic brauchen asDay beide, und
 * invoiceLogic importiert bereits aus allocationLogic — laege asDay dort,
 * entstuende ein Importzyklus.
 *
 * NICHT fuer den Tag einer LEISTUNG: welcher Tag, welcher Zeitraum, welcher
 * Monat ein Protokolleintrag ist, entscheidet in der Abrechnung seit
 * S5a (vor Stufe 3) `buchungstag` unten — der Berliner Tag (Betreiber-Entscheid
 * 1 vom 02.10.2026). asDay bleibt fuer Belegangaben, die bereits reine Tage
 * sind (Leistungs-, Stichtags- und Zeitraumfelder des Belegs), und fuer
 * eingefrorene Zuordnungstage, die so bleiben, wie sie ausgestellt wurden.
 */
export const asDay = (iso?: string): string | undefined => iso?.slice(0, 10);

// ════════════════════════════════════════════════════════════════════════════
// DER BUCHUNGSTAG (S5d) — der Kalendertag einer Leistung in Europe/Berlin
// ════════════════════════════════════════════════════════════════════════════
//
// `asDay` oben beantwortet eine ANDERE Frage (Zeitstempel gegen ein als reiner
// Tag geschriebenes Zeitraumende vergleichbar machen) und schneidet dafuer den
// UTC-Text. Fuer „an welchem TAG fand die Begehung statt" ist das falsch:
// lokale Mitternacht des 05.05. ist in MESZ `2026-05-04T22:00:00Z`.
//
// Bis S5d rechnete jeder Tagesriegel seinen Tag selbst — `book()` und das
// Nachbuchen in Geraetezeit, der Nachtlauf (`istTagGedeckt`, auf GitHub in
// UTC) mit dem Textschnitt, der Sendedialog (`isSameDay`) mit UTC-Praefix
// ODER Geraetezeit. Dieselbe Buchung konnte so an zwei Stellen an zwei
// verschiedenen Tagen liegen. Seit S5d rechnen alle mit `buchungstag`, und das
// ist genau die Journal-Spalte `entry_day` (serviceJournal.entryDay delegiert
// hierher; Nr. 60/76 rechnen dieselbe Regel in SQL, die Abweichungssicht
// vergleicht `entry_day` jede Nacht).
//
// Hier und nicht in serviceJournal.ts: diese Datei ist das Blattmodul, das
// reportBooking und unbookedReportsLogic importieren duerfen, ohne sich den
// Supabase-Client einzufangen.

/** Reines Tagesdatum ohne Zeitanteil, `2026-03-05`. */
const IST_TAG = /^\d{4}-\d{2}-\d{2}$/;
/** Ein ISO-Zeitstempel, egal ob mit `T` oder mit Leerzeichen. */
const IST_ZEITSTEMPEL = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
/** Traegt er eine Zonenangabe? `Z`, `+02:00`, `+0200`, `-05`. */
const HAT_ZONE = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

/** Die feste Zone der Anwendung — NIE die des Geraets, NIE die des Servers. */
const BERLIN = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

/** `YYYY-MM-DD` aus den Bestandteilen — nie ueber das Gebietsschema geraten. */
const berlinerTag = (d: Date): string => {
    const teile = BERLIN.formatToParts(d);
    const teil = (typ: string) => teile.find(t => t.type === typ)?.value ?? '';
    return `${teil('year')}-${teil('month')}-${teil('day')}`;
};

/**
 * Der Kalendertag in Europe/Berlin, `YYYY-MM-DD` — oder `null`, wenn der Wert
 * fehlt oder unlesbar ist.
 *
 * - Reiner Tag (`2026-05-05`): unveraendert.
 * - Zeitstempel OHNE Zone (`2026-05-05T08:30:00`): die Wanduhrzeit des
 *   Betriebs, also bereits Berliner Zeit — der Tag steht in der Zeichenkette
 *   und geht nicht durch ein Date-Objekt (das laese Geraetezeit).
 * - Zeitstempel MIT Zone (`…Z`, `+02:00`): in Europe/Berlin umgerechnet.
 *
 * Grenze gegen die SQL-Fassung (`service_journal_local_day`, Nr. 61): die
 * prueft Tag und Uhrzeit auf Gueltigkeit und liefert fuer `…T24:00:00`,
 * `…T99:99`, einen 30. Februar oder freien Text `null`; hier gilt der Tag aus
 * dem Text bzw. was `Date` daraus liest. Keiner der Schreibpfade erzeugt solche
 * Werte (sie schreiben `toISOString()`, `YYYY-MM-DD` oder `berlinZeitpunkt`);
 * fuer alle gueltigen Werte sind beide Fassungen gleich.
 */
export const buchungstag = (value?: string | null): string | null => {
    const roh = String(value ?? '').trim();
    if (!roh) return null;
    if (IST_TAG.test(roh)) return roh.slice(0, 10);
    if (IST_ZEITSTEMPEL.test(roh) && !HAT_ZONE.test(roh)) return roh.slice(0, 10);
    const d = new Date(roh);
    if (isNaN(d.getTime())) return null;
    return berlinerTag(d);
};

/** Wanduhrzeit in Europe/Berlin, vierundzwanzig Stunden (nie „24:00"). */
const BERLIN_VOLL = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});

/** Versatz Berlin gegen UTC in Millisekunden zu einem Zeitpunkt (MEZ 1 h, MESZ 2 h). */
const berlinVersatz = (ms: number): number => {
    const teile = BERLIN_VOLL.formatToParts(new Date(ms));
    const teil = (typ: string) => Number(teile.find(t => t.type === typ)?.value);
    return Date.UTC(teil('year'), teil('month') - 1, teil('day'), teil('hour'), teil('minute'), teil('second')) - ms;
};

/**
 * Ein Sortierschluessel auf der BERLINER Wanduhr: `YYYY-MM-DDTHH:MM:SS`.
 *
 * Fuer jede Reihenfolge, die neben `buchungstag` steht (Codex-Review S5a,
 * 03.10.2026, P2): wer im Zeitraum nach dem Berliner Tag entscheidet, aber
 * nach dem ROHTEXT sortiert, ordnet `2026-05-31T22:00:00Z` (Berlin: 01.06.)
 * vor `2026-05-31T23:30:00` (ohne Zone, Berlin: 31.05.) — und zieht dann die
 * Juni-Menge vor der Mai-Menge. Dieselben Regeln wie `buchungstag`: ein reiner
 * Tag gilt als Mitternacht, ein Zeitstempel ohne Zone bleibt, wie er
 * geschrieben ist, einer mit Zone wird auf Berlin umgerechnet. `null`, wenn
 * nichts davon lesbar ist.
 */
export const berlinerSortierzeit = (value?: string | null): string | null => {
    const roh = String(value ?? '').trim();
    if (!roh) return null;
    if (IST_TAG.test(roh)) return `${roh}T00:00:00`;
    if (IST_ZEITSTEMPEL.test(roh) && !HAT_ZONE.test(roh)) {
        const ohneBruch = roh.replace(' ', 'T').replace(/\.\d+$/, '');
        return (ohneBruch + ':00').slice(0, 19);
    }
    const d = new Date(roh);
    if (isNaN(d.getTime())) return null;
    const teile = BERLIN_VOLL.formatToParts(d);
    const teil = (typ: string) => teile.find(t => t.type === typ)?.value ?? '00';
    return `${teil('year')}-${teil('month')}-${teil('day')}T${teil('hour')}:${teil('minute')}:${teil('second')}`;
};

/**
 * Der Zeitpunkt (ISO, UTC) zu Tag und Uhrzeit AUF DER BAUSTELLE — also in
 * Europe/Berlin, nicht in der Zeitzone des Geraets. Die Gegenrichtung zu
 * `buchungstag`: `buchungstag(berlinZeitpunkt(t, u)) === t` fuer jede gueltige
 * Eingabe.
 *
 * Gebraucht von den Schreibpfaden der Begehungsbuchung (`book()`, Nachbuchen).
 * Bis S5d bauten sie `new Date(`${tag}T${zeit}:00`)` — das liest JavaScript als
 * GERAETEZEIT. Im Inland dasselbe; auf einem Geraet in New York lag eine
 * Begehung vom 05.05. um 20:00 danach am 06.05. (Berlin), und der Tagesriegel,
 * der seit S5d in Berlin rechnet, haette den eingegebenen Tag nicht gesehen.
 *
 * `null` bei unerwarteter Form (Tag nicht `YYYY-MM-DD`, Uhrzeit nicht `HH:MM`)
 * oder ungueltiger Kalendertag) — der Aufrufer faellt dann auf seinen
 * bisherigen Weg zurueck. In der Luecke der Fruehjahrsumstellung (02:00–03:00
 * gibt es in Berlin nicht) landet der Wert eine Stunde spaeter, in der
 * doppelten Herbststunde auf der ersten (Sommerzeit-)Fassung; der Tag bleibt
 * in beiden Faellen richtig.
 */
export const berlinZeitpunkt = (tag?: string | null, uhrzeit?: string | null): string | null => {
    const t = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(tag ?? '').trim());
    const u = /^(\d{2}):(\d{2})$/.exec(String(uhrzeit ?? '').trim());
    if (!t || !u) return null;
    const stunde = Number(u[1]);
    const minute = Number(u[2]);
    if (stunde > 23 || minute > 59) return null;
    const jahr = Number(t[1]);
    const monat = Number(t[2]);
    const tagZahl = Number(t[3]);
    const wand = Date.UTC(jahr, monat - 1, tagZahl, stunde, minute);
    // `Date.UTC` rechnet einen 30. Februar still in den 2. Maerz um — ein
    // ungueltiger Kalendertag ist hier `null`, kein anderer Tag.
    const probe = new Date(wand);
    if (isNaN(wand) || probe.getUTCFullYear() !== jahr || probe.getUTCMonth() + 1 !== monat || probe.getUTCDate() !== tagZahl) {
        return null;
    }
    // Die beiden Versaetze des Tages (Morgen- und Abendseite). Passt einer,
    // gilt der FRUEHERE Zeitpunkt — in der doppelten Herbststunde (02:00–03:00
    // am letzten Oktobersonntag) also die Sommerzeit, genau wie
    // `new Date('…T02:30:00')` auf einem Geraet in Berlin. Passt keiner, liegt
    // die Uhrzeit in der Luecke der Fruehjahrsumstellung: eine Stunde spaeter.
    const a = wand - berlinVersatz(wand - 12 * 3_600_000);
    const b = wand - berlinVersatz(wand + 12 * 3_600_000);
    const frueher = Math.min(a, b);
    const spaeter = Math.max(a, b);
    for (const ms of [frueher, spaeter]) {
        if (ms + berlinVersatz(ms) === wand) return new Date(ms).toISOString();
    }
    return new Date(spaeter).toISOString();
};

/** Ein Zeitstempel MIT Zonenangabe, wie Postgres/PostgREST ihn schreiben. */
const ZEITSTEMPEL_MIT_ZONE =
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(\.\d+)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)$/i;

/**
 * Derselbe Zeitpunkt, geschrieben als BERLINER WANDUHRZEIT mit ausdruecklichem
 * Versatz: `2026-07-31T22:00:00+00:00` → `2026-08-01T00:00:00+02:00`, im
 * Winter `+01:00`. Nur die Schreibweise aendert sich — `new Date()` liest
 * denselben Moment, `asDay` (Textschnitt) liest danach den Berliner Tag,
 * also `buchungstag` = `entry_day`.
 *
 * Gebraucht vom Rueckweg aus dem Journal (serviceJournal
 * `journalToServiceTransactions`, V3 vor S5a): `entry_at` ist `timestamptz`,
 * PostgREST gibt es in UTC heraus, und ein im JSON reiner Tag `2026-08-01` ist
 * im Journal die Berliner Mitternacht (Nr. 60/103) — als UTC-Text
 * `2026-07-31T22:00:00+00:00`. Jeder `asDay`-Leser (Zeitraum, Stichtag,
 * Zuordnungstag, Monatsaufteilung `slice(0, 7)`) saehe den Vortag, am
 * Monatsersten den Vormonat.
 *
 * - Sekundenbruchteile bleiben ZEICHENGLEICH stehen (auch Mikrosekunden aus
 *   Postgres): der Versatz ist eine ganze Stunde und aendert sie nie.
 * - Die doppelte Herbststunde ist eindeutig, weil der Versatz dabeisteht
 *   (`02:30+02:00` vor `02:30+01:00`); die Fruehjahrsluecke kann aus einem
 *   Zeitpunkt nicht entstehen.
 * - `null` fuer alles ohne Zone (reiner Tag, zonenlose Wanduhrzeit — das IST
 *   schon Berliner Zeit, siehe `buchungstag`), fuer Unlesbares und fuer
 *   ungueltige Kalendertage oder Uhrzeiten. Der Aufrufer behaelt dann seinen
 *   Rohwert, also das Verhalten von vorher.
 *
 * GRENZE: Als TEXT sortiert ist die Schreibweise chronologisch bis auf die
 * doppelte Herbststunde — `02:15+01:00` steht vor `02:30+02:00`, obwohl es
 * eine Dreiviertelstunde spaeter liegt. Wer strikt nach Zeit ordnen muss,
 * vergleicht `Date.parse`, nicht den Text.
 */
export const berlinerZeitstempel = (value?: string | null): string | null => {
    const m = ZEITSTEMPEL_MIT_ZONE.exec(String(value ?? '').trim());
    if (!m) return null;
    const [, j, mo, t, h, mi, s, bruch, zone] = m;
    const jahr = Number(j);
    const monat = Number(mo);
    const tagZahl = Number(t);
    const stunde = Number(h);
    const minute = Number(mi);
    const sekunde = Number(s ?? '0');
    if (stunde > 23 || minute > 59 || sekunde > 59) return null;

    // Der Versatz der Eingabe in Minuten (`Z`, `+00`, `+0000`, `+00:00`).
    let versatzEin = 0;
    if (zone.toUpperCase() !== 'Z') {
        const z = /^([+-])(\d{2}):?(\d{2})?$/.exec(zone);
        if (!z) return null;
        const zh = Number(z[2]);
        const zm = Number(z[3] ?? '0');
        if (zh > 23 || zm > 59) return null;
        versatzEin = (z[1] === '-' ? -1 : 1) * (zh * 60 + zm);
    }

    const wandEin = Date.UTC(jahr, monat - 1, tagZahl, stunde, minute, sekunde);
    // Wie in `berlinZeitpunkt`: ein 30. Februar ist `null`, kein 2. Maerz.
    const probe = new Date(wandEin);
    if (isNaN(wandEin) || probe.getUTCFullYear() !== jahr || probe.getUTCMonth() + 1 !== monat || probe.getUTCDate() !== tagZahl) {
        return null;
    }

    const ms = wandEin - versatzEin * 60_000;
    const versatz = berlinVersatz(ms);
    // Vor 1893 rechnet Berlin in Ortszeit mit Sekundenversatz; eine solche
    // Angabe ist hier kein Leistungsdatum, sondern ein Tippfehler.
    if (!Number.isFinite(versatz) || versatz % 60_000 !== 0) return null;
    const wand = new Date(ms + versatz);
    const wandJahr = wand.getUTCFullYear();
    if (wandJahr < 1000 || wandJahr > 9999) return null;

    const minuten = versatz / 60_000;
    const betrag = Math.abs(minuten);
    const zoneAus = `${minuten < 0 ? '-' : '+'}${String(Math.floor(betrag / 60)).padStart(2, '0')}:${String(betrag % 60).padStart(2, '0')}`;
    return `${wand.toISOString().slice(0, 19)}${bruch ?? ''}${zoneAus}`;
};

// ════════════════════════════════════════════════════════════════════════════
// DIE KENNUNG EINES PROTOKOLLEINTRAGS IM VERGLEICH
// ════════════════════════════════════════════════════════════════════════════

/** Eine uuid in beliebiger Schreibweise (A–F wie a–f). */
const IST_UUID_KENNUNG = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Eine Kennung fuer den Vergleich: beschnitten; eine uuid zusaetzlich
 * kleingeschrieben. NUR die uuid (Codex-Review 02.10.2026, P2): eine
 * Alt-Kennung („Alt-A" neben „alt-a") ist Text, und zwei Schreibweisen sind
 * zwei Eintraege — kleingeschrieben verschmoelzen sie, und eine Ruecknahme von
 * „alt-a" traefe womoeglich „Alt-A".
 *
 * Warum es diese Regel ueberhaupt braucht: eine freie uuid im JSON bleibt so
 * stehen, wie sie geschrieben wurde (`journalIdDecision` behaelt auch
 * Grossbuchstaben), die uuid-Spalte des Journals gibt sie aber kleingeschrieben
 * zurueck. Dieselbe Leistung hat damit je nach Lesepfad zwei Schreibweisen —
 * und die Zuordnung der Vorbelege (`allocations.entryId`) nennt die, die beim
 * Ausstellen galt.
 *
 * EINE Regel, EIN Ort: das Journal (`serviceJournal`, lebende Zeilen zu einer
 * JSON-id) und die Zuordnung (`allocationLogic.allocatedByEntry` und jede Stelle,
 * die darin nachschlaegt) vergleichen ueber genau diese Funktion. Sie steht in
 * diesem Blattmodul, damit allocationLogic sie importieren kann, ohne sich den
 * Supabase-Client einzufangen; serviceJournal reicht sie unter demselben Namen
 * weiter. Zwei Fassungen liefen frueher oder spaeter auseinander — dann faende
 * das Journal die Zeile, die Zuordnung aber nicht, und eine abgerechnete Menge
 * gaelte auf dem Journal-Weg wieder als offen (Doppelforderung in S5a Stufe 3).
 */
export const kennungFuerVergleich = (wert: unknown): string => {
    const s = String(wert ?? '').trim();
    return IST_UUID_KENNUNG.test(s) ? s.toLowerCase() : s;
};
